import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Trash2, MapPin, DollarSign, Bed, Bath, Bell, BellOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../context/AuthContext';
import { userAPI } from '../services/api';
import { COVERAGE_AREAS } from '../data/mockData';
import { toast } from 'sonner';

const SavedSearchesPage = () => {
  const { user, savedSearches, deleteSearch, refreshSavedSearches } = useAuth();
  const [updatingAlerts, setUpdatingAlerts] = useState({});

  const getAreaName = (areaId) => {
    const area = COVERAGE_AREAS.find(a => a.id === areaId);
    return area ? area.name : 'All Areas';
  };

  const toggleEmailAlerts = async (searchId, currentValue) => {
    setUpdatingAlerts(prev => ({ ...prev, [searchId]: true }));
    try {
      await userAPI.updateSearch(searchId, { email_alerts: !currentValue });
      await refreshSavedSearches();
      toast.success(currentValue ? 'Email alerts disabled' : 'Email alerts enabled! You\'ll receive daily updates when new properties match.');
    } catch (error) {
      console.error('Failed to update email alerts:', error);
      toast.error('Failed to update email alerts');
    } finally {
      setUpdatingAlerts(prev => ({ ...prev, [searchId]: false }));
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Search size={48} className="text-gray-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Sign in to view saved searches</h1>
          <p className="text-gray-500 mb-6">Save your search criteria and get notified of new listings</p>
          <Link to="/login">
            <Button className="bg-red-600 hover:bg-red-700">Sign In</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Saved Searches</h1>
          <p className="text-gray-600">{savedSearches.length} saved search{savedSearches.length !== 1 ? 'es' : ''}</p>
        </div>

        {/* Email Alerts Info Banner */}
        <div className="bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <Bell className="text-red-600 mt-0.5" size={20} />
            <div>
              <h3 className="font-semibold text-red-900">Daily Property Alerts</h3>
              <p className="text-sm text-red-700 mt-1">
                Enable email alerts on any saved search to receive daily notifications when new properties match your criteria.
              </p>
            </div>
          </div>
        </div>

        {savedSearches.length === 0 ? (
          <div className="text-center py-16">
            <Search size={48} className="text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No saved searches</h2>
            <p className="text-gray-500 mb-6">Search for properties and save your criteria for quick access</p>
            <Link to="/search">
              <Button className="bg-red-600 hover:bg-red-700">Start Searching</Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {savedSearches.map((search) => (
              <Card key={search.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-3">
                        <MapPin size={18} className="text-red-600" />
                        <h3 className="font-semibold text-gray-900">
                          {search.name || (search.location ? getAreaName(search.location) : 'All Areas')}
                        </h3>
                        {search.email_alerts && (
                          <Badge className="bg-green-100 text-green-700 border-green-200">
                            <Bell size={12} className="mr-1" />
                            Alerts On
                          </Badge>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap gap-2 mb-3">
                        {search.minPrice && search.minPrice !== 'none' && (
                          <Badge variant="outline">
                            <DollarSign size={12} className="mr-1" />
                            Min ${Number(search.minPrice).toLocaleString()}
                          </Badge>
                        )}
                        {search.maxPrice && search.maxPrice !== 'none' && (
                          <Badge variant="outline">
                            <DollarSign size={12} className="mr-1" />
                            Max ${Number(search.maxPrice).toLocaleString()}
                          </Badge>
                        )}
                        {search.beds && search.beds !== 'any' && (
                          <Badge variant="outline">
                            <Bed size={12} className="mr-1" />
                            {search.beds}+ Beds
                          </Badge>
                        )}
                        {search.baths && search.baths !== 'any' && (
                          <Badge variant="outline">
                            <Bath size={12} className="mr-1" />
                            {search.baths}+ Baths
                          </Badge>
                        )}
                        {search.propertyType && search.propertyType !== 'all' && (
                          <Badge variant="outline">
                            {search.propertyType}
                          </Badge>
                        )}
                      </div>

                      <p className="text-xs text-gray-400">
                        Saved {new Date(search.created_at || search.createdAt).toLocaleDateString()}
                        {search.last_alert_sent && (
                          <span className="ml-2">• Last alert: {new Date(search.last_alert_sent).toLocaleDateString()}</span>
                        )}
                      </p>
                    </div>

                    <div className="flex items-center gap-3">
                      {/* Email Alerts Toggle */}
                      <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                        <label className="text-sm text-gray-600 cursor-pointer flex items-center gap-2" htmlFor={`alerts-${search.id}`}>
                          {search.email_alerts ? <Bell size={14} className="text-green-600" /> : <BellOff size={14} className="text-gray-400" />}
                          <span className="hidden sm:inline">Alerts</span>
                        </label>
                        <Switch
                          id={`alerts-${search.id}`}
                          checked={search.email_alerts || false}
                          onCheckedChange={() => toggleEmailAlerts(search.id, search.email_alerts)}
                          disabled={updatingAlerts[search.id]}
                          className="data-[state=checked]:bg-green-600"
                        />
                      </div>

                      <Link to={`/search?${new URLSearchParams({
                        ...(search.location && search.location !== 'all' ? { location: search.location } : {}),
                        ...(search.propertyType && search.propertyType !== 'all' ? { propertyType: search.propertyType } : {}),
                        ...(search.beds && search.beds !== 'any' ? { beds: search.beds } : {}),
                        ...(search.baths && search.baths !== 'any' ? { baths: search.baths } : {}),
                        ...(search.minPrice && search.minPrice !== 'none' ? { minPrice: search.minPrice } : {}),
                        ...(search.maxPrice && search.maxPrice !== 'none' ? { maxPrice: search.maxPrice } : {}),
                        ...(search.keyword ? { keyword: search.keyword } : {}),
                      }).toString()}`}>
                        <Button className="bg-red-600 hover:bg-red-700">
                          <Search size={16} className="mr-2" />
                          Search
                        </Button>
                      </Link>
                      <Button 
                        variant="outline" 
                        size="icon"
                        onClick={() => deleteSearch(search.id)}
                        className="text-gray-400 hover:text-red-600 hover:border-red-600"
                      >
                        <Trash2 size={18} />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SavedSearchesPage;
